import { useAuthActions, useAuthState } from '@creditornot/wolt-auth/react';
import React, { useEffect, useMemo, useReducer, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';

import * as fb from 'core/facebook';
import * as woltAuthApi from 'modules/wolt-auth/api';

import LandingPage from './LandingPage';
import {
  EmailLoginHandler,
  FacebookLoginHandler,
  GoogleLoginHandler,
  LoginContext,
  LoginContextValue,
} from '../LoginContext';
import { useNextPage } from '../hooks/useNextPage';
import { LoginMethod, LoginStatus } from '../types';

type State = {
  activeLoginMethod: LoginMethod | null;
  loginStatus: LoginStatus;
};

type Action =
  | 'endEmailLogin'
  | 'failEmailLogin'
  | 'failFacebookLogin'
  | 'failGoogleLogin'
  | 'startEmailLogin'
  | 'startFacebookLogin'
  | 'startGoogleLogin';

const initialState: State = {
  activeLoginMethod: null,
  loginStatus: 'idle',
};

const reducer = (_: State, action: Action): State => {
  switch (action) {
    case 'startEmailLogin': {
      return {
        activeLoginMethod: 'email',
        loginStatus: 'loading',
      };
    }
    case 'failEmailLogin': {
      return {
        activeLoginMethod: 'email',
        loginStatus: 'error',
      };
    }
    case 'endEmailLogin': {
      return initialState;
    }
    case 'startFacebookLogin': {
      return {
        activeLoginMethod: 'facebook',
        loginStatus: 'loading',
      };
    }
    case 'failFacebookLogin': {
      return {
        activeLoginMethod: 'facebook',
        loginStatus: 'error',
      };
    }
    case 'startGoogleLogin': {
      return {
        activeLoginMethod: 'google',
        loginStatus: 'loading',
      };
    }
    case 'failGoogleLogin': {
      return {
        activeLoginMethod: 'google',
        loginStatus: 'error',
      };
    }
  }
};

const Login = () => {
  const [{ activeLoginMethod, loginStatus }, dispatch] = useReducer(reducer, initialState);
  const { status: authStatus, user } = useAuthState();
  const { nextPage } = useNextPage();

  const { login } = useAuthActions();
  const [googleLoginError] = useQueryParam('error', StringParam);

  // Remember the initial status upon page load.
  // TODO: We shouldn't even render this component for a logged-in user.
  const initialAuthStatusRef = useRef(authStatus);

  const startEmailLogin = () => {
    dispatch('startEmailLogin');
  };

  const failEmailLogin = () => {
    dispatch('failEmailLogin');
  };

  const endEmailLogin = () => {
    dispatch('endEmailLogin');
  };

  const startFacebookLogin = () => {
    dispatch('startFacebookLogin');
  };

  const failFacebookLogin = () => {
    dispatch('failFacebookLogin');
  };

  const startGoogleLogin = () => {
    dispatch('startGoogleLogin');
  };

  const failGoogleLogin = () => {
    dispatch('failGoogleLogin');
  };

  const handleFacebookLogin: FacebookLoginHandler = async () => {
    startFacebookLogin();
    let accessToken;
    try {
      accessToken = await fb.login();
    } catch (error) {
      failFacebookLogin();
      return;
    }
    if (!accessToken) {
      failFacebookLogin();
      return;
    }
    try {
      await login({
        grantType: 'facebook_token',
        facebookToken: accessToken,
        audience: 'merchant-admin',
      });
    } catch (error) {
      failFacebookLogin();
    }
  };

  const handleGoogleLogin: GoogleLoginHandler = () => {
    startGoogleLogin();
    window.location.assign(process.env.GOOGLE_LOGIN_URI);
  };

  const handleEmailLogin: EmailLoginHandler = async data => {
    const { email, captchaHeader } = data;
    startEmailLogin();
    try {
      await woltAuthApi.requestEmailLoginLink(email, {
        ...captchaHeader,
      });
    } catch (error) {
      failEmailLogin();
      return;
    }
    endEmailLogin();
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    const googleLoginToken = searchParams.get('token');
    const emailLoginToken = searchParams.get('emailLoginToken');

    if (initialAuthStatusRef.current !== 'logged-out') {
      return;
    }

    if (googleLoginToken) {
      const consumeGoogleLoginToken = async () => {
        startGoogleLogin();
        try {
          await login({
            grantType: 'authorization_code',
            code: googleLoginToken,
          });
        } catch (error) {
          console.error('Failed to login via Google token', error);
          failGoogleLogin();
        }
      };

      consumeGoogleLoginToken();
      return;
    }

    if (emailLoginToken) {
      const consumeEmailLoginToken = async () => {
        startEmailLogin();
        try {
          await login({
            grantType: 'email_login',
            audience: 'merchant-admin',
            token: emailLoginToken,
          });
        } catch (error) {
          console.error('Failed to login via email token', error);
          failEmailLogin();
        }
      };

      consumeEmailLoginToken();
      return;
    }
  }, [login]);

  useEffect(() => {
    if (googleLoginError) {
      failGoogleLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginContextValue: LoginContextValue = useMemo(
    () => ({
      activeMethod: activeLoginMethod,
      onEmailLogin: handleEmailLogin,
      onFacebookLogin: handleFacebookLogin,
      onGoogleLogin: handleGoogleLogin,
      status: loginStatus,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeLoginMethod, loginStatus],
  );

  if (user) {
    return <Redirect to={nextPage} />;
  }

  return (
    <LoginContext.Provider value={loginContextValue}>
      <LandingPage />
    </LoginContext.Provider>
  );
};

export default Login;
